import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { useSettings } from '../context/SettingsContext';
import LazyImage from './LazyImage';

const DesktopHero = () => {
  const navigate = useNavigate();
  const { systemSettings, resolveMediaUrl } = useSettings();
  const [currentSlide, setCurrentSlide] = useState(0);

  const banners = Array.isArray(systemSettings?.banners) ? systemSettings.banners : [];
  const hasBanners = banners.length > 0;
  
  const displayBanners = hasBanners ? banners : [];
  const autoChangeMsRaw = Number(systemSettings?.banner_auto_change_ms);
  const autoChangeMs = Number.isFinite(autoChangeMsRaw) ? Math.min(600000, Math.max(500, autoChangeMsRaw)) : 5000;

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % displayBanners.length);
  }, [displayBanners.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + displayBanners.length) % displayBanners.length);
  }, [displayBanners.length]);

  useEffect(() => {
    if (displayBanners.length <= 1) return;

    // Auto-play interval
    const interval = setInterval(() => {
      nextSlide();
    }, autoChangeMs);

    return () => clearInterval(interval);
  }, [displayBanners.length, nextSlide, autoChangeMs]);

  const handleBannerClick = (banner) => {
    const raw = String(banner?.redirect_url || '').trim();
    if (!raw) return;

    if (/^https?:\/\//i.test(raw)) {
      window.location.assign(raw);
    } else {
      const path = raw.startsWith('/') ? raw : `/${raw}`;
      navigate(path);
    }
  };

  if (!hasBanners) {
    return (
      <div className="w-full h-[300px] bg-gray-100 flex items-center justify-center text-gray-400">
        <div className="text-center">
          <p className="text-lg font-medium">بنری برای نمایش وجود ندارد</p>
        </div>
      </div>
    );
  }

  const getSlideStyle = (index) => {
    const isActive = index === currentSlide;
    const isPrev = index === (currentSlide - 1 + displayBanners.length) % displayBanners.length;
    const isNext = index === (currentSlide + 1) % displayBanners.length;
    
    let transform = 'scale(0.85) translateX(0)';
    let zIndex = 0;
    let opacity = 0.6;
    let blur = 'blur(2px)';

    if (isActive) {
      transform = 'scale(1) translateX(0)';
      zIndex = 20;
      opacity = 1;
      blur = 'blur(0)';
    } else if (isPrev) {
      transform = 'scale(0.85) translateX(-115%)'; // Move to left with spacing
      zIndex = 10;
      opacity = 0.7;
      blur = 'blur(1px)';
    } else if (isNext) {
      transform = 'scale(0.85) translateX(115%)'; // Move to right with spacing
      zIndex = 10;
      opacity = 0.7;
      blur = 'blur(1px)';
    }

    return {
      transform,
      zIndex,
      opacity,
      filter: blur,
      transition: 'all 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
    };
  };

  return (
    <div className="relative w-full h-[350px] md:h-[450px] lg:h-[500px] overflow-hidden group bg-gray-50 dir-ltr flex items-center justify-center py-8">
      {/* Slides Container */}
      <div className="relative w-full max-w-7xl h-full flex items-center justify-center">
        {displayBanners.map((banner, index) => {
          // Calculate if this slide should be visible (active, prev, next)
          const isActive = index === currentSlide;
          const isPrev = index === (currentSlide - 1 + displayBanners.length) % displayBanners.length;
          const isNext = index === (currentSlide + 1) % displayBanners.length;
          
          // Only render relevant slides for better performance if many banners
          if (!isActive && !isPrev && !isNext && displayBanners.length > 3) return null;

          return (
            <div
              key={index}
              className="absolute w-[80%] md:w-[70%] h-full rounded-2xl shadow-2xl overflow-hidden cursor-pointer"
              style={getSlideStyle(index)}
              onClick={() => isActive && handleBannerClick(banner)}
            >
              {banner.image_url ? (
                <LazyImage
                  src={resolveMediaUrl(banner.image_url)}
                  alt={`Banner ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-400">
                  تصویر موجود نیست
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Navigation Arrows */}
      {displayBanners.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); prevSlide(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-black/30 hover:bg-black/50 text-white p-3 rounded-full backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100 transform hover:scale-110"
            aria-label="Previous Slide"
          >
            <ChevronLeftIcon className="w-6 h-6 md:w-8 md:h-8" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); nextSlide(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-black/30 hover:bg-black/50 text-white p-3 rounded-full backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100 transform hover:scale-110"
            aria-label="Next Slide"
          >
            <ChevronRightIcon className="w-6 h-6 md:w-8 md:h-8" />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {displayBanners.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex space-x-2 space-x-reverse">
          {displayBanners.map((_, index) => (
            <button
              key={index}
              onClick={(e) => { e.stopPropagation(); setCurrentSlide(index); }}
              className={`h-3 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'bg-white w-8 shadow-md' 
                  : 'bg-white/50 w-3 hover:bg-white/80'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DesktopHero;
